/**
 * Sound cues, vibration and a gamepad's rumble for what happens in the local run. The engine
 * decides *when* something sounds (here); the audio module decides *how* (src/audio).
 */
import type { JumpKind, Mode, SimEvent } from '@cube-rush/shared';
import type { SoundCue } from '../audio/types';
import type { PadRumble } from '../input/gamepads';

/**
 * The mode a kind of jump belongs to (a cube jumps, a ball flips, a UFO flaps, a robot thrusts,
 * a spider teleports).
 */
const JUMP_MODE: Readonly<Record<JumpKind, Mode>> = {
  ground: 'cube',
  air: 'cube',
  flip: 'ball',
  flap: 'ufo',
  robot: 'robot',
  teleport: 'spider',
};

/**
 * The sound of a simulation event of the local player (null: silent). `slower`: the event is a
 * speed portal that slows the player down (the level knows: see LevelGuide.speedAt).
 */
export function soundCueFor(event: SimEvent, slower = false): SoundCue | null {
  switch (event.type) {
    case 'jump':
      return { kind: 'jump', mode: JUMP_MODE[event.kind] };
    case 'orb':
      return { kind: 'orb', color: event.color };
    case 'pad':
      return { kind: 'pad', color: event.color };
    case 'portal':
      return slower
        ? { kind: 'portal', portal: event.portal, slower }
        : { kind: 'portal', portal: event.portal };
    case 'death':
      return { kind: 'death' };
    case 'respawn':
      return { kind: 'respawn' };
    case 'levelComplete':
      return { kind: 'levelComplete', level: event.level };
    case 'pickup':
      return { kind: 'pickup', boost: event.boost, ignored: event.ignored };
    case 'boostStart':
      return { kind: 'boostStart', boost: event.boost };
    case 'boostEnd':
      return { kind: 'boostEnd', boost: event.boost };
    case 'land':
    case 'levelStart':
    case 'raceFinished':
      // Landing is silent; the next level's music and the finish fanfare are handled apart.
      return null;
  }
}

/** Vibration patterns (ms on / off). */
export const HAPTIC_PATTERNS = {
  death: [40],
  levelComplete: [25, 70, 25],
} as const satisfies Record<string, readonly number[]>;

/** A gamepad's rumble (the pad played with last, where the browser can make it shake). */
export const PAD_RUMBLE = {
  death: { durationMs: 160, strong: 0.7, weak: 0.4 },
} as const satisfies Record<string, PadRumble>;

/** Vibrates where the browser can (Android); a silent no-op elsewhere (iOS). */
export function vibrate(pattern: readonly number[]): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([...pattern]);
    }
  } catch {
    // Some browsers throw when vibration is blocked (no user gesture yet): ignore.
  }
}
